import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppUserDefaultStore, ALL_RESTAURANTS_ID } from '../../../../stores/appUserDefaultStore';
import { useCostPriceStore } from '../../../../stores/appCostPriceStore';
import { useSubrecipesStore } from '../../../../stores/appSubrecipesStore';
import { useCostPriceCategoriesStore } from '../../../../stores/appCostPriceCategoriesStore';
import { Subrecipe } from '../../../../services/subrecipes/subrecipesService';
import { AutomationRunSources, loadAutomationRunSources } from './automationRunSources';
import { isPracticeActive } from '../../../onboarding/practice/practiceGuards';
import { useSetupReviewContext } from '../../accountSetup/SetupReviewContext';
import { setupRequest } from '../../../../services/accountSetup/accountSetupService';

/** The run is resolved only inside the selected restaurant; stale store data never grants access. */
export function useAutomationRun(runId: string | undefined) {
  const setupReview = useSetupReviewContext();
  const restrictedRunId = setupReview?.restrictedToRestaurant ? setupReview.runId : undefined;
  const selectedId = useAppUserDefaultStore(s => s.restaurant?.id);
  const restaurantId = selectedId && selectedId !== ALL_RESTAURANTS_ID && !isPracticeActive() ? selectedId : undefined;
  const costs = useCostPriceStore();
  const categoriesStore = useCostPriceCategoriesStore();
  const subs = useSubrecipesStore();
  const [loadedFor, setLoadedFor] = useState<string>();
  const [sourceData, setSourceData] = useState<AutomationRunSources & { restaurantId: string; key: string }>();
  const [sourceError, setSourceError] = useState(false);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [revision, setRevision] = useState(0);
  const reload = useCallback(async () => {
    if (!restaurantId) return;
    await Promise.all([
      useCostPriceStore.getState().load(restaurantId, { force: true }),
      useCostPriceStore.getState().loadCustomIngredients(restaurantId, { force: true }),
      useCostPriceStore.getState().loadAutomationRuns(restaurantId),
      useCostPriceCategoriesStore.getState().load(restaurantId, { force: true }),
      useSubrecipesStore.getState().load(restaurantId, { force: true }),
    ]);
    setRevision(value => value + 1);
  }, [restaurantId]);
  useEffect(() => {
    let current = true;
    setLoadedFor(undefined);
    if (restaurantId) void reload().finally(() => { if (current) setLoadedFor(restaurantId); });
    return () => { current = false; };
  }, [restaurantId, reload]);

  const scopedDishes = useMemo(() => costs.currentRestaurantId === restaurantId
    ? costs.costPriceList.filter(d => d.restaurantId === restaurantId) : [], [costs.costPriceList, costs.currentRestaurantId, restaurantId]);
  // Amount edits do not change the dependency graph and must not restart source loading.
  const sourceKey = JSON.stringify(scopedDishes.map(dish => [dish.id, dish.ingredients.map(ingredient =>
    [ingredient.sourceRestaurantId, ingredient.nestedCostPriceId, ingredient.subrecipeId,
      ingredient.custom ? [ingredient.articleId, ingredient.productName, ingredient.platform] : undefined])]));
  useEffect(() => {
    let current = true;
    if (!restaurantId || loadedFor !== restaurantId) return;
    setSourceError(false);
    setSourcesLoading(true);
    void loadAutomationRunSources(restaurantId, scopedDishes, restrictedRunId ? () =>
      setupRequest<AutomationRunSources>(`/api/account-setup/restaurants/${encodeURIComponent(restaurantId)}/review-sources?runId=${encodeURIComponent(restrictedRunId)}`) : undefined).then(data => {
      if (current) setSourceData({ restaurantId, key: sourceKey, ...data });
    }).catch(error => {
      console.error('Failed to load review source ingredients:', error);
      if (current) setSourceError(true);
    }).finally(() => { if (current) setSourcesLoading(false); });
    return () => { current = false; };
    // scopedDishes is represented by sourceKey; quantities do not affect source discovery.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, loadedFor, sourceKey, revision, restrictedRunId]);

  const run = costs.automationRunsRestaurantId === restaurantId
    ? costs.automationRuns.find(r => r.id === runId && r.restaurantId === restaurantId && !r.completedAt) : undefined;
  const dishes = useMemo(() => run ? scopedDishes.filter(d => d.automationRunId === run.id) : [], [scopedDishes, run]);
  const subrecipes = useMemo(() => {
    const map = new Map<string, Subrecipe>();
    if (sourceData && sourceData.restaurantId === restaurantId) sourceData.subrecipes.forEach(sub => map.set(sub.id, sub));
    if (subs.currentRestaurantId === restaurantId) subs.subrecipesById.forEach((value, key) => map.set(key, value));
    return map;
  }, [subs.currentRestaurantId, subs.subrecipesById, sourceData, restaurantId]);
  const allDishes = useMemo(() => {
    const map = new Map((sourceData && sourceData.restaurantId === restaurantId ? sourceData.dishes : []).map(dish => [dish.id, dish]));
    scopedDishes.forEach(dish => map.set(dish.id, dish));
    return Array.from(map.values());
  }, [scopedDishes, sourceData, restaurantId]);
  return {
    restaurantId, run, dishes, allDishes, subrecipes,
    customIngredients: [
      ...(costs.customIngredientsRestaurantId === restaurantId ? costs.customIngredients : []),
      ...(sourceData && sourceData.restaurantId === restaurantId ? sourceData.customIngredients.filter(custom => custom.restaurantId !== restaurantId) : []),
    ],
    categories: categoriesStore.currentRestaurantId === restaurantId ? categoriesStore.categories : [],
    loading: !!restaurantId && (loadedFor !== restaurantId || (!sourceError && sourceData?.restaurantId !== restaurantId)),
    refreshing: costs.isLoading || costs.isLoadingCustomIngredients || costs.isLoadingAutomationRuns || categoriesStore.isLoading || subs.isLoading || sourcesLoading || sourceData?.key !== sourceKey,
    error: !!(costs.error || costs.customIngredientsError || costs.automationRunsError || categoriesStore.error || subs.error || sourceError),
    reload,
  };
}
